import React, { ChangeEvent } from "react";
import {
  Box,
  Button,
  IconButton,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CloudUploadOutlinedIcon from "@material-ui/icons/CloudUploadOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import {
  LegalDocument,
  LegalDocumentFileName,
  LegalDocumentFiles,
} from "../types";
import useAssistedAffiliationStyles from "../styles";

type LegalDocumentsUploadProps = {
  documents: LegalDocument[];
  files: LegalDocumentFiles;
  errors: Partial<Record<LegalDocumentFileName, string>>;
  onFilesChange: (field: LegalDocumentFileName) => (
    event: ChangeEvent<HTMLInputElement>
  ) => void;
  onRemoveFile: (field: LegalDocumentFileName, fileIndex: number) => void;
  onAddDocument: () => void;
};

const LegalDocumentsUpload = ({
  documents,
  files,
  errors,
  onFilesChange,
  onRemoveFile,
  onAddDocument,
}: LegalDocumentsUploadProps) => {
  const classes = useAssistedAffiliationStyles();

  return (
    <>
      {documents.map((document) => {
        const documentFiles = files[document.field];
        const inputId = `assisted-affiliation-${document.field}`;
        const error = errors[document.field];

        return (
          <Box key={document.field}>
            <Box className={classes.documentRow}>
              <Typography variant="subtitle2">
                {document.label}
                {document.required ? (
                  <span className={classes.requiredMark}> *</span>
                ) : null}
              </Typography>
              <Box className={classes.legalDocumentFiles}>
                {documentFiles.length ? (
                  documentFiles.map((file, index) => (
                    <Box className={classes.legalDocumentFile} key={`${file.name}-${index}`}>
                      <Box className={classes.uploadInfo}>
                        <Box className={classes.uploadFileIcon}>
                          <CloudUploadOutlinedIcon fontSize="small" />
                        </Box>
                        <Box>
                          <Typography className={classes.uploadTitle}>{file.name}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {Math.ceil(file.size / 1024)} KB
                          </Typography>
                        </Box>
                      </Box>
                      <IconButton
                        size="small"
                        aria-label={`Eliminar ${file.name}`}
                        onClick={() => onRemoveFile(document.field, index)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))
                ) : (
                  <Box className={classes.uploadInfo}>
                    <Box className={classes.uploadEmptyIcon}>
                      <CloudUploadOutlinedIcon fontSize="small" />
                    </Box>
                    <Box>
                      <Typography className={classes.uploadTitle}>
                        Adjunta un archivo
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        JPG o PDF hasta 25MB
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
              <Box className={classes.documentStatus}>
                {documentFiles.length ? (
                  <CheckCircleIcon className={classes.greenIcon} fontSize="small" />
                ) : null}
                <label htmlFor={inputId}>
                  <Button
                    component="span"
                    variant="outlined"
                    size="small"
                    className={classes.attachButton}
                  >
                    Adjuntar archivo
                  </Button>
                </label>
              </Box>
              <input
                id={inputId}
                className={classes.hiddenInput}
                type="file"
                accept=".jpg,.jpeg,.pdf"
                multiple
                required={document.required}
                onChange={onFilesChange(document.field)}
              />
            </Box>
            {error ? (
              <Typography variant="caption" color="error">
                {error}
              </Typography>
            ) : null}
          </Box>
        );
      })}
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        className={classes.addDocumentButton}
        onClick={onAddDocument}
      >
        Adjuntar documento
      </Button>
    </>
  );
};

export default LegalDocumentsUpload;
